'use client';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useRef, useState, type ReactNode } from "react";
import Slider, { type Settings } from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";

export default function ScrollBox({
    children,
    slidesToShowSmall = 2,
    slidesToShowLarge = 4,
}: {
    children: ReactNode[] | ReactNode;
    slidesToShowSmall?: number;
    slidesToShowLarge?: number;
}) {
    const [activeSlide, setActiveSlide] = useState(0);

    const sliderRef = useRef<Slider | null>(null);

    const settings: Settings = {
        infinite: true,
        speed: 500,
        slidesToShow: slidesToShowLarge,
        slidesToScroll: 1,
        arrows: false,
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: slidesToShowSmall,
                    slidesToScroll: 1,
                },
            },
        ],
        beforeChange: (_, next) => {
            setActiveSlide(next);
        },
        afterChange: (current) => {
            setActiveSlide(current);

            // Fix aria-hidden for focusable elements in all slides
            const slider = sliderRef.current;
            if (slider) {
                const slides = slider?.innerSlider?.list?.querySelectorAll(".slick-slide");
                if (slides) {
                    slides.forEach((slide, index) => {
                        const isHidden = slide.getAttribute("aria-hidden") === "true";
                        slide.querySelectorAll<HTMLElement>("button, a, input, select, textarea, div")?.forEach((el) => {
                            if (el && el?.setAttribute) {
                                el?.setAttribute("tabindex", isHidden ? "-1" : "0");
                            }
                        });
                    });
                }
            }
        },
    };

    const next = () => {
        sliderRef.current?.slickNext();
    };

    const previous = () => {
        sliderRef.current?.slickPrev();
    };

    return (
        <div className="h-full w-full relative overflow-visible">
            {/* Small screens: horizontal scroll */}
            <div className="overflow-x-auto md:hidden flex px-1 space-x-2 relative pb-4">
                {children}
            </div>

            {/* Medium and up: Slider */}
            <div className="hidden md:block relative">
                <button
                    aria-label="Scroll to previous card"
                    onClick={previous}
                    className="absolute block w-10 h-10 left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 p-2 backdrop-blur shadow-md rounded-full"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <Slider
                    ref={sliderRef}
                    {...settings}
                    className="h-full"
                >
                    {children}
                </Slider>
                <button
                    aria-label="Scroll to next card"
                    onClick={next}
                    className="absolute block w-10 h-10 right-2 top-1/2 transform translate-x-1/2 -translate-y-1/2 z-20 p-2 backdrop-blur shadow-md rounded-full"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            </div>
        </div>
    );
}
